/**
 * Derive a taste profile ("smak") from a user's high-rated tasting notes.
 *
 * Naive but effective for MVP: tally country/region/grape/keyword frequencies
 * weighted by rating, then surface the top occurrences as the user's leanings.
 * As the backend gains embeddings, this can swap to a proper vector profile.
 */
import type { Bottle, TastingNote } from '@/lib/api';
import type { Taste } from './occasion-presets';

export interface SmakProfile {
  topRegions: string[];
  topCountries: string[];
  topGrapes: string[];
  averageRating: number;
  topTastes: Taste[];
  highlights: { bottle: Bottle; rating: number; comment?: string }[];
}

const TASTE_KEYWORDS: { taste: Taste; words: string[] }[] = [
  { taste: 'full-bodied', words: ['fyldig', 'kraftig', 'tung', 'full-bodied', 'big'] },
  { taste: 'medium-bodied', words: ['middels', 'medium', 'balansert'] },
  { taste: 'light', words: ['lett', 'light', 'frisk og lett'] },
  { taste: 'fruity', words: ['fruktig', 'fruit', 'bær', 'kirsebær', 'plomme', 'morell'] },
  { taste: 'oaky', words: ['eik', 'oak', 'vanilje', 'vanilla', 'røyk'] },
  { taste: 'mineral', words: ['mineral', 'flint', 'salt', 'salinitet'] },
  { taste: 'spicy', words: ['krydder', 'pepper', 'kanel', 'nellik'] },
  { taste: 'tannic', words: ['tanniner', 'tanninrik', 'tannic', 'astringent'] },
  { taste: 'crisp', words: ['frisk', 'crisp', 'syre', 'høy syre'] },
  { taste: 'sweet', words: ['søt', 'sweet', 'restsødme'] },
  { taste: 'sparkling', words: ['perler', 'mousse', 'bobler', 'champagne'] },
  { taste: 'aromatic', words: ['aromatisk', 'aromatic', 'parfymert', 'rosenblader'] },
  { taste: 'earthy', words: ['jord', 'jordpreget', 'sopp', 'underskog', 'forna'] },
];

export function buildSmakProfile(bottles: Bottle[], notesByBottleId: Map<string, TastingNote[]>): SmakProfile {
  // Pull all notes ≥ 4 stars + their bottles.
  const highlights: SmakProfile['highlights'] = [];
  let weightedSum = 0;
  let weightCount = 0;

  const regionTally = new Map<string, number>();
  const countryTally = new Map<string, number>();
  const grapeTally = new Map<string, number>();
  const tasteTally = new Map<Taste, number>();

  for (const bottle of bottles) {
    const notes = notesByBottleId.get(bottle.id) ?? [];
    if (notes.length === 0) continue;

    const best = notes.reduce<TastingNote | null>(
      (acc, n) => (acc == null || n.rating1to5 > acc.rating1to5 ? n : acc),
      null,
    );
    if (!best) continue;

    weightedSum += best.rating1to5;
    weightCount += 1;

    if (best.rating1to5 >= 4) {
      const weight = best.rating1to5;
      if (bottle.region) regionTally.set(bottle.region, (regionTally.get(bottle.region) ?? 0) + weight);
      if (bottle.country) countryTally.set(bottle.country, (countryTally.get(bottle.country) ?? 0) + weight);
      bottle.grapeVarieties.forEach((g) =>
        grapeTally.set(g, (grapeTally.get(g) ?? 0) + weight),
      );
      const haystack = `${best.comment ?? ''} ${bottle.notes ?? ''}`.toLowerCase();
      for (const { taste, words } of TASTE_KEYWORDS) {
        if (words.some((w) => haystack.includes(w))) {
          tasteTally.set(taste, (tasteTally.get(taste) ?? 0) + weight);
        }
      }
      highlights.push({ bottle, rating: best.rating1to5, comment: best.comment });
    }
  }

  return {
    topRegions: top(regionTally, 5),
    topCountries: top(countryTally, 3),
    topGrapes: top(grapeTally, 6),
    averageRating: weightCount > 0 ? weightedSum / weightCount : 0,
    topTastes: top(tasteTally, 6) as Taste[],
    highlights: highlights.sort((a, b) => b.rating - a.rating).slice(0, 6),
  };
}

function top<K>(tally: Map<K, number>, n: number): K[] {
  return [...tally.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([k]) => k);
}
